import {
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  Skeleton,
} from '@monksflow/monks-ui';
import { twMerge } from 'tailwind-merge';
import { LOADING_ROWS_COUNT, TABLE_CLASSES } from './Table.constants';
import type { Column, TableProps, TableShellProps } from './Table.types';
import { TableFilterMenu } from './TableFilterMenu';
import { TablePaginationBar } from './TablePaginationBar';
import { TableRow } from './TableRow';
import { TableSortCell } from './TableSortCell';

const TableShell = <TData, TSortKey extends string, TFilters>({
  columns,
  sortBy,
  sortDirection,
  pagination,
  onSort,
  filtering,
  emptyContent,
  children,
  containerClassName,
}: TableShellProps<TData, TSortKey, TFilters>) => (
  <div data-testid="table-shell" className={twMerge(TABLE_CLASSES.container, containerClassName)}>
    {/* Kept at its natural height so the pagination bar below it is never pushed off. */}
    <div className="shrink-0">
      <Datatable>
        <DatatableHeader>
          <TableRow>
            {columns.map((column: Column<TData, TSortKey>) => {
              const key = String(column.key);
              const filterMenu = filtering && column.filterable && (
                <TableFilterMenu
                  config={filtering.filterConfigs[key]}
                  filters={filtering.filters}
                  onFiltersChange={filtering.onFiltersChange}
                  label={String(column.label ?? column.key)}
                />
              );

              return column.sortable ? (
                <TableSortCell<TSortKey>
                  key={key}
                  className={column.className}
                  sortKey={(column.sortKey ?? key) as TSortKey}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  adornment={filterMenu}
                  onSort={onSort}
                >
                  {column.label}
                </TableSortCell>
              ) : (
                <DatatableHead
                  key={key}
                  showBorder={false}
                  className={twMerge(TABLE_CLASSES.headCell, column.className)}
                >
                  <div className="flex items-center gap-1">
                    {column.label}
                    {filterMenu}
                  </div>
                </DatatableHead>
              );
            })}
          </TableRow>
        </DatatableHeader>
        {children}
      </Datatable>
    </div>

    {emptyContent}

    {pagination && <TablePaginationBar {...pagination} />}
  </div>
);

export function Table<TData, TSortKey extends string = string & keyof TData, TFilters = unknown>({
  isLoading,
  columns,
  rows,
  sortBy,
  sortDirection,
  pagination,
  renderBodyRow,
  onSort,
  filtering,
  emptyContent,
  containerClassName,
}: TableProps<TData, TSortKey, TFilters>) {
  const sharedShellProps: Omit<
    TableShellProps<TData, TSortKey, TFilters>,
    'children' | 'pagination' | 'emptyContent'
  > = {
    columns,
    sortBy,
    sortDirection,
    onSort,
    filtering,
    containerClassName,
  };

  if (isLoading && !rows.length) {
    return (
      <TableShell<TData, TSortKey, TFilters> {...sharedShellProps} pagination={pagination}>
        <DatatableBody>
          {Array.from({ length: LOADING_ROWS_COUNT }).map((_, rowIndex) => (
            <TableRow key={`loading-row-${rowIndex}`}>
              {columns.map((column: Column<TData, TSortKey>) => (
                <DatatableCell key={String(column.key)} className={column.className}>
                  <Skeleton className="h-4 w-full" />
                </DatatableCell>
              ))}
            </TableRow>
          ))}
        </DatatableBody>
      </TableShell>
    );
  }

  const showEmpty = !rows.length && emptyContent;

  return (
    <TableShell<TData, TSortKey, TFilters>
      {...sharedShellProps}
      pagination={showEmpty ? undefined : pagination}
      emptyContent={showEmpty ? emptyContent : undefined}
    >
      <DatatableBody>{!showEmpty && rows.map(renderBodyRow)}</DatatableBody>
    </TableShell>
  );
}
